'use client';

import { isEmpty } from '@/utils/isEmpty';
import { ComponentProps, FC, useEffect, useEffectEvent, useState } from 'react';
import { Fetcher } from 'swr';
import useSWRImmutable from 'swr/immutable';
import Cookies from 'universal-cookie';
import { shallow } from 'zustand/shallow';

import { COOKIE_RECENTLY_VIEWED } from 'src/constants/cookie.constants';
import { PA_DEFAULT_RATE } from 'src/data/TypesenseApi/constants';
import { BaseVehiclesPayload } from 'src/data/TypesenseApi/types';
import { ProductCard } from 'src/general/components/ProductCard/ProductCard';
import { ProductCardLoading } from 'src/general/components/ProductCard/ProductCardLoading';
import { sortBySku } from 'src/general/helpers/sortBySku';
import { PlpMarketingTile } from 'src/plp/components/PlpMarketingTile/PlpMarketingTile';
import { recentFilterSearchStore } from 'src/stores/recentFilterSearchStore';
import { pushToDataLayerFeatureEngagement } from 'src/utils/pushToDataLayerFeatureEngagement';

import { RecentSearches } from '../RecentSearches/RecentSearches';
import { FlexibleSection } from '../flexibleContent.type';
import { getSectionClasses } from '../getSectionClasses.helper';
import { RecentlyViewedCarouselSection } from './CarouselSection';

const MIN_RECENTLY_VIEWED = 1;

interface Props extends FlexibleSection {
  sectionData: {
    title?: string;
    marketingItems?: ComponentProps<typeof PlpMarketingTile>[];
  };
}

const skusFetcher: Fetcher<BaseVehiclesPayload, { id: string; skus: string[] }> = async ({
  skus,
}): Promise<BaseVehiclesPayload> => {
  if (skus.length) {
    const response = await fetch(`/api/plp?skus=${skus.join(',')}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return response.json();
  }
  return {
    results: [],
    total: 0,
  };
};

export const RecentlyViewedCarousel: FC<Props> = ({ sectionData, sectionBackground }) => {
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);
  /**
   * Display condition for recently viewed carousel: at least 2 cars without a marketing tile or at least 1 car with a marketing tile
   */
  const { data, isLoading } = useSWRImmutable({ id: 'fetchVehiclesBySku', skus: recentlyViewed }, skusFetcher);
  const { recentFilterSearches } = recentFilterSearchStore(
    (state) => ({
      recentFilterSearches: state.recentFilterSearches,
    }),
    shallow,
  );

  const onLoaded = useEffectEvent(() => {
    const cookies = new Cookies();
    const recentlyViewedData = cookies.get(COOKIE_RECENTLY_VIEWED);
    if (recentlyViewedData && Array.isArray(recentlyViewedData)) {
      setRecentlyViewed(recentlyViewedData);
    }
  });

  useEffect(() => {
    onLoaded();
  }, []);

  if (recentlyViewed.length < MIN_RECENTLY_VIEWED && recentFilterSearches.length < MIN_RECENTLY_VIEWED) {
    return null;
  }

  const tiles = generateTiles(isLoading, recentlyViewed, data?.results, sectionData.marketingItems);

  if (tiles.length <= MIN_RECENTLY_VIEWED && recentFilterSearches.length < MIN_RECENTLY_VIEWED) {
    return null;
  }

  return (
    <section className={getSectionClasses(sectionBackground)}>
      <div className="flex flex-col gap-10 md:flex-row">
        {recentFilterSearches.length > 0 && (
          <RecentSearches recentSearches={recentFilterSearches} className="flex-1 md:min-h-px md:flex-auto" />
        )}
        <div className="w-full min-w-0">
          {!isEmpty(sectionData.title) && (
            <header className="mb-8 md:mb-10" data-testid="recently-viewed-carousel-header">
              <h2 className="c-fs-h4 font-extrabold">{sectionData.title}</h2>
            </header>
          )}
          <RecentlyViewedCarouselSection tiles={tiles} carouselName="recently-viewed-carousel" />
        </div>
      </div>
    </section>
  );
};

const generateTiles = (
  isLoading: boolean,
  skus: string[],
  results?: BaseVehiclesPayload['results'],
  marketingItems?: ComponentProps<typeof PlpMarketingTile>[],
): ComponentProps<typeof RecentlyViewedCarouselSection>['tiles'] => {
  if (isLoading || !results) {
    return [...new Array(4).keys()].map((item) => ({
      id: `${item}`,
      tile: <ProductCardLoading key={`loading-${item}`} variant="default" />,
    }));
  }

  // Sort the results based on the skus
  const sortedResults = sortBySku(results, skus);

  const tiles = sortedResults.map((product, index) => ({
    id: product.sku,
    tile: (
      <ProductCard
        carData={product}
        weeklyRepayment={product.weeklyRepayment ?? 0}
        defaultRate={PA_DEFAULT_RATE}
        variant="default"
        onCardClick={() => {
          pushToDataLayerFeatureEngagement({
            event: 'feature_engagement_click',
            payload: {
              type: 'click',
              feature: 'recentlyViewed',
              component: 'carousel',
              index,
            },
          });
        }}
      />
    ),
  }));

  if (marketingItems) {
    const marketingTiles = marketingItems.map((tile) => ({
      id: tile.title ?? '',
      tile: <PlpMarketingTile {...tile} variant="carousel" />,
    }));
    tiles.push(...marketingTiles);
  }

  return tiles;
};
